import React, { useState, useEffect, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { suggestionApi, employeeApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { formatDateTime } from '../lib/utils';
import SubmissionForm from '../components/SubmissionForm';
import BenefitTag from '../components/BenefitTag';
import { toast } from 'sonner';

export default function SuggestionsTab({ employeeId, canEdit }) {
  const [rows, setRows] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [emp, setEmp] = useState(null);

  // The employee is already known on this tab, so the form never asks for one.
  useEffect(() => {
    employeeApi.get(employeeId).then((r) => setEmp(r.data)).catch(() => setEmp(null));
  }, [employeeId]);

  const load = useCallback(async () => {
    try { const r = await suggestionApi.history(employeeId); setRows(r.data?.months || []); }
    catch (e) { toast.error('Failed to load suggestions'); }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const review = async (s) => {
    try { await suggestionApi.review(s.id, { status: 'reviewed' }); load(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="bg-white border border-[#E6E9F0] rounded-xl p-6">
      <h2 className="font-display text-xl font-bold tracking-[-0.02em] text-[#172554] mb-4">Suggestions</h2>
      {canEdit && (
        <div className="mb-4">
          <Button data-testid="add-suggestion" onClick={() => setFormOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />Add suggestion
          </Button>
        </div>
      )}
      {rows.length === 0 ? <p className="text-[#98A1B2] py-8 text-center">No suggestions yet. Employees can submit via their QR code.</p> : (
        <div className="space-y-5">
          {rows.map((m) => (
            <div key={m.period}>
              <div className="flex items-baseline gap-2 mb-2">
                <h3 className="text-sm font-semibold text-[#11182B]">{m.month_label}</h3>
                <span className="text-[11px] text-[#98A1B2]">{m.count} submission{m.count === 1 ? '' : 's'}</span>
              </div>
              <div className="space-y-2">
                {m.items.map((s) => (
                  <div key={s.id} className="border border-[#F2F4F8] rounded-lg p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {s.title && <p className="text-sm font-semibold text-[#11182B]">{s.title}</p>}
                        <p className="text-sm text-[#11182B] whitespace-pre-wrap">{s.text}</p>
                        <BenefitTag row={s} />
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${s.status === 'reviewed' ? 'bg-[#E7F7EF] text-[#15A36E]' : 'bg-[#FBF7EC] text-[#8A5A12]'}`}>{s.status === 'reviewed' ? 'Reviewed' : 'New'}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-2">
                      <span className="text-[11px] text-[#98A1B2]">Submitted {formatDateTime(s.created_at)} · {s.source === 'self' ? 'Self (QR)' : 'HR'}</span>
                      {canEdit && s.status !== 'reviewed' && <button onClick={() => review(s)} className="text-xs text-[#2563EB] hover:underline">Mark reviewed</button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New suggestion</DialogTitle></DialogHeader>
          <SubmissionForm mode="suggestion" employee={emp}
                          onSaved={() => { setFormOpen(false); load(); }}
                          onCancel={() => setFormOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
